/**
 * Answer control for a BPMN user task with `tri:uxElement="select"`: a dropdown whose options the
 * server loads live from the tool the process model names (e.g. the Odoo companies of Dr. Sasse).
 *
 * The component only picks — the server reloads the options on completion and takes value and
 * label from there, so nothing shown here is trusted (tri-server docs/bpm-select-task.md).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { TaskSelectOption } from '../UserAPI';

export interface TaskSelectAnswerProps {
    /** Loads the options; called on mount and on retry. */
    loadOptions: () => Promise<TaskSelectOption[]>;
    /** The user confirmed a choice. */
    onSubmit: (option: TaskSelectOption) => void;
    disabled?: boolean;
}

export const TaskSelectAnswer: React.FC<TaskSelectAnswerProps> = ({ loadOptions, onSubmit, disabled }) => {
    const { t } = useTranslation();
    const [options, setOptions] = useState<TaskSelectOption[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string>('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setOptions(null);
        setError(null);
        setSelected('');
        loadOptions()
            .then((loaded) => {
                if (!cancelled) setOptions(loaded);
            })
            .catch((err: any) => {
                // '' = failed without a message of its own; translated at render time, so the
                // effect does not depend on `t` (a new `t` would reload and drop the choice).
                if (!cancelled) setError(err?.message || '');
            });
        return () => {
            cancelled = true;
        };
        // `attempt` re-runs the load on retry; loadOptions is recreated by the parent per task.
    }, [loadOptions, attempt]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    if (error !== null) {
        return (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-destructive" data-testid="chat-task-select-error">
                <span>{error || t('chatBotPage.selectOptionsFailed', { defaultValue: 'Die Auswahl konnte nicht geladen werden.' })}</span>
                <Button type="button" variant="outline" size="sm" onClick={retry} aria-label={t('chatBotPage.selectRetry', { defaultValue: 'Erneut laden' })}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>
        );
    }

    if (options === null) {
        return (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="chat-task-select-loading">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('chatBotPage.selectLoading', { defaultValue: 'Auswahl wird geladen …' })}
            </div>
        );
    }

    const chosen = options.find((option) => String(option.value) === selected);

    return (
        <div className="flex flex-1 items-center gap-2">
            <Select value={selected} onValueChange={setSelected} disabled={disabled}>
                <SelectTrigger className="h-8 flex-1" data-testid="chat-task-select" aria-label={t('chatBotPage.selectPlaceholder', { defaultValue: 'Bitte auswählen' })}>
                    <SelectValue placeholder={t('chatBotPage.selectPlaceholder', { defaultValue: 'Bitte auswählen' })} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((option) => (
                        <SelectItem key={String(option.value)} value={String(option.value)}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Button
                type="button"
                size="sm"
                className="gap-2"
                data-testid="chat-task-select-send"
                aria-label={t('chatBotPage.completeTask')}
                disabled={disabled || !chosen}
                onClick={() => chosen && onSubmit(chosen)}
            >
                <Check className="h-4 w-4" />
            </Button>
        </div>
    );
};

export default TaskSelectAnswer;
